import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import { isProduction } from '@/admin/lib/is-production';
import type {
  PaymentRequestDetail,
  PaymentRequestListItem,
  PaymentRequestRoutingFilter,
  PaymentRequestStatusFilter,
  PaymentRequestSendStatus,
  PaymentRequestsListResult,
} from './types';
import { ROUTING_LABELS, SEND_STATUS_LABELS, SEND_STATUS_TONE } from './types';

interface Props {
  refreshKey: number;
  onRefresh: () => void;
  initialStatus: PaymentRequestStatusFilter;
  initialRouting: PaymentRequestRoutingFilter;
}

const formatDateTime = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('es-CL', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Santiago',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short', year: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const ROUTING_OPTIONS: { value: PaymentRequestRoutingFilter; label: string }[] = [
  { value: 'all', label: 'Todos los canales' },
  { value: 'pac', label: 'PAC' },
  { value: 'lups', label: 'Lups' },
];

// `pac_native` no se ofrece como filtro: el admin lo encuentra filtrando
// por canal = PAC (que también incluye los pac_active y pac externos).
const STATUS_OPTIONS: { value: PaymentRequestStatusFilter; label: string }[] = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'sent', label: 'Enviado' },
  { value: 'in_collection', label: 'En cobro' },
  { value: 'resolved', label: 'Cobrado' },
  { value: 'send_failed', label: 'Envío fallido' },
  { value: 'disabled', label: 'Deshabilitado' },
  { value: 'deleted', label: 'Eliminado' },
];

export default function PaymentRequestsTable({ refreshKey, onRefresh, initialStatus, initialRouting }: Props) {
  const [routing, setRouting] = useState<PaymentRequestRoutingFilter>(initialRouting);
  const [sendStatus, setSendStatus] = useState<PaymentRequestStatusFilter>(initialStatus);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [data, setData] = useState<PaymentRequestsListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<PaymentRequestDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    setRouting(initialRouting);
    setSendStatus(initialStatus);
    setPage(1);
  }, [initialRouting, initialStatus]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const base = getAdminEndpointUrl('paymentRequests', 'list');
      const params = new URLSearchParams();
      if (routing !== 'all') params.set('routing', routing);
      if (sendStatus !== 'all') params.set('sendStatus', sendStatus);
      if (search.trim()) params.set('search', search.trim());
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      const url = `${base}?${params.toString()}`;
      const result = await adminHttpClient.get<PaymentRequestsListResult>(url);
      setData(result);
    } catch (err) {
      console.error('Error cargando payment requests', err);
      setData({ page, pageSize, total: 0, items: [] });
    } finally {
      setLoading(false);
    }
  }, [routing, sendStatus, search, page, pageSize]);

  useEffect(() => {
    void fetchList();
  }, [fetchList, refreshKey]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleClearFilters = () => {
    setRouting('all');
    setSendStatus('all');
    setSearch('');
    setSearchInput('');
    setPage(1);
  };

  const openDetail = async (item: PaymentRequestListItem) => {
    setLoadingDetail(true);
    try {
      const url = getAdminEndpointUrl('paymentRequests', 'detail', { id: item.id });
      const detail = await adminHttpClient.get<PaymentRequestDetail>(url);
      setSelected(detail);
    } catch (err) {
      console.error('Error cargando detalle', err);
      alert(err instanceof Error ? err.message : 'No se pudo cargar el detalle');
    } finally {
      setLoadingDetail(false);
    }
  };

  const refreshDetail = useCallback(async (id: string) => {
    const url = getAdminEndpointUrl('paymentRequests', 'detail', { id });
    const detail = await adminHttpClient.get<PaymentRequestDetail>(url);
    setSelected(detail);
  }, []);

  const totalPages = useMemo(() => (data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1), [data]);

  return (
    <>
      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        {/* Toolbar */}
        <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1.5">
              {ROUTING_OPTIONS.map((opt) => {
                const isActive = routing === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setRouting(opt.value);
                      setPage(1);
                    }}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider transition-all ${
                      isActive
                        ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                        : 'border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <div className="ml-auto flex items-center gap-2">
              <select
                value={sendStatus}
                onChange={(e) => {
                  setSendStatus(e.target.value as PaymentRequestStatusFilter);
                  setPage(1);
                }}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 focus:border-slate-400 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <button
                onClick={onRefresh}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
              >
                <span className="material-symbols-outlined text-[14px]">refresh</span>
                Refrescar
              </button>
            </div>
          </div>

          <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[16px] text-slate-400">search</span>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Buscar por id, lupsRequestId, connectorId, advanceRequestId, installmentDetailId…"
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 font-mono text-xs text-slate-700 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
              />
            </div>
            <button type="submit" className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
              Buscar
            </button>
            {(routing !== 'all' || sendStatus !== 'all' || search) && (
              <button type="button" onClick={handleClearFilters} className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                limpiar
              </button>
            )}
          </form>
        </div>

        {/* Tabla */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:border-slate-800 dark:bg-slate-950/40">
                <th className="py-3 pl-5 pr-4">Routing</th>
                <th className="py-3 px-4">Estado</th>
                <th className="py-3 px-4">id</th>
                <th className="py-3 px-4">Adelanto / Cuota</th>
                <th className="py-3 px-4 text-right">Monto</th>
                <th className="py-3 px-4">Objetivo</th>
                <th className="py-3 px-4">Abandona</th>
                <th className="py-3 px-4">Cobrado</th>
                <th className="py-3 px-4 text-right pr-5"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="py-12 text-center">
                    <span className="inline-flex items-center gap-2 font-mono text-xs text-slate-400">
                      <span className="material-symbols-outlined animate-spin text-[16px]">progress_activity</span>
                      cargando payment requests…
                    </span>
                  </td>
                </tr>
              )}

              {!loading && data && data.items.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-16 text-center">
                    <div className="flex flex-col items-center gap-2 text-slate-400">
                      <span className="material-symbols-outlined text-3xl">inbox</span>
                      <p className="font-mono text-xs">Sin payment requests con los filtros actuales</p>
                    </div>
                  </td>
                </tr>
              )}

              {!loading &&
                data?.items.map((row) => {
                  const tone = SEND_STATUS_TONE[row.sendStatus];
                  // Solo 2 canales visibles en admin: PAC (amber) o Lups (blue).
                  // Más prominente que un ring sutil para que el admin distinga al
                  // golpe de vista qué cobranza pasa por dónde.
                  const isPac = row.routing === 'pac';
                  const routingClasses = isPac
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';
                  return (
                    <tr
                      key={row.id}
                      className="group cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50/60 dark:border-slate-800 dark:hover:bg-slate-800/40"
                      onClick={() => void openDetail(row)}
                    >
                      <td className="py-3 pl-5 pr-4">
                        <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-mono text-[11px] font-bold uppercase tracking-wider ${routingClasses}`}>
                          <span className="material-symbols-outlined text-[14px]">{isPac ? 'account_balance' : 'sync_alt'}</span>
                          {ROUTING_LABELS[row.routing]}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${tone.bg} ${tone.text} ring-1 ring-inset ${tone.ring}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
                          {SEND_STATUS_LABELS[row.sendStatus]}
                        </span>
                        {row.sendAttempts > 1 && (
                          <span className="ml-1 inline-flex items-center font-mono text-[10px] text-amber-600 dark:text-amber-400">
                            ×{row.sendAttempts}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <code className="font-mono text-[11px] text-slate-700 dark:text-slate-300 block truncate max-w-[140px]" title={row.id}>{row.id}</code>
                        {row.lupsRequestId && (
                          <code className="font-mono text-[10px] text-slate-400 block truncate max-w-[140px]" title={row.lupsRequestId}>{row.lupsRequestId}</code>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <code className="font-mono text-[10px] text-slate-500 truncate block max-w-[140px]" title={row.advanceRequestId}>{row.advanceRequestId}</code>
                        <code className="font-mono text-[10px] text-slate-400 truncate block max-w-[140px]" title={row.installmentDetailId}>{row.installmentDetailId}</code>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-mono text-xs tabular-nums text-slate-700 dark:text-slate-300">${Number(row.monto).toLocaleString('es-CL')}</span>
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-mono text-[11px] text-slate-600 dark:text-slate-300">{formatDate(row.fechaObjetivo)}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-mono text-[11px] text-slate-500">{formatDate(row.abandonmentDate)}</p>
                      </td>
                      <td className="py-3 px-4">
                        {row.collectedAt ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                            <span className="material-symbols-outlined text-[14px]">check_circle</span>
                            <span className="font-mono text-[10px]">{formatDateTime(row.collectedAt)}</span>
                          </span>
                        ) : row.abandonedAt ? (
                          <span className="inline-flex items-center gap-1 text-slate-500">
                            <span className="material-symbols-outlined text-[14px]">cancel</span>
                            <span className="font-mono text-[10px]">abandonado</span>
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right pr-5">
                        <span className="material-symbols-outlined text-[16px] text-slate-300 group-hover:translate-x-0.5 group-hover:text-slate-500 transition-transform">chevron_right</span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {data && data.total > 0 && (
          <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3 font-mono text-[11px] text-slate-500 dark:border-slate-800">
            <p>
              Mostrando <span className="text-slate-700 dark:text-slate-200">{(data.page - 1) * data.pageSize + 1}</span>–
              <span className="text-slate-700 dark:text-slate-200">{Math.min(data.page * data.pageSize, data.total)}</span> de{' '}
              <span className="text-slate-700 dark:text-slate-200 tabular-nums">{data.total}</span>
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="rounded-md border border-slate-200 px-2 py-1 hover:bg-slate-50 disabled:opacity-30 dark:border-slate-700 dark:hover:bg-slate-800">
                ← prev
              </button>
              <span className="tabular-nums">
                {page} / {totalPages}
              </span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="rounded-md border border-slate-200 px-2 py-1 hover:bg-slate-50 disabled:opacity-30 dark:border-slate-700 dark:hover:bg-slate-800">
                next →
              </button>
            </div>
          </div>
        )}
      </section>

      {loadingDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
          <span className="material-symbols-outlined animate-spin text-3xl text-white">progress_activity</span>
        </div>
      )}

      {selected && (
        <DetailDrawer
          lpr={selected}
          onClose={() => setSelected(null)}
          onRefreshDetail={() => refreshDetail(selected.id)}
          onRefreshList={onRefresh}
        />
      )}
    </>
  );
}

function DetailDrawer({
  lpr,
  onClose,
  onRefreshDetail,
  onRefreshList,
}: {
  lpr: PaymentRequestDetail;
  onClose: () => void;
  onRefreshDetail: () => Promise<void>;
  onRefreshList: () => void;
}) {
  const tone = SEND_STATUS_TONE[lpr.sendStatus];
  const [forcing, setForcing] = useState(false);
  // Solo se puede forzar el cobro contra un PR ya creado en Lups (sendStatus=SENT
  // con lupsRequestId presente, sin haberse cobrado/abandonado). El botón está
  // oculto en producción por isProduction(); el backend valida lo mismo.
  const canForceLupsCharge =
    !isProduction() &&
    lpr.routing === 'lups' &&
    lpr.sendStatus === 'sent' &&
    !!lpr.lupsRequestId &&
    !lpr.collectedAt &&
    !lpr.abandonedAt &&
    !lpr.disabledAt &&
    !lpr.deletedAt;

  const handleForceLupsCharge = async () => {
    if (!confirm('¿Forzar cobro Lups ahora?\nGenera la orden de pago y dispara el cargo contra la TC OneClick por defecto del cliente.')) return;
    setForcing(true);
    try {
      const url = getAdminEndpointUrl('paymentRequests', 'forceLupsCharge', { id: lpr.id });
      const result = await adminHttpClient.post<{ id: string; orderUuid: string; orderNumber: string; cardLastFour: string }>(url);
      alert(`Orden generada en Lups: ${result.orderNumber}\nuuid: ${result.orderUuid}\ncargada a TC ****${result.cardLastFour}\n\nEl resultado final llegará por webhook.`);
      await onRefreshDetail();
      onRefreshList();
    } catch (err) {
      console.error('Error forzando cobro Lups', err);
      alert(err instanceof Error ? err.message : 'No se pudo forzar el cobro a Lups');
    } finally {
      setForcing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-white dark:bg-slate-900 shadow-2xl overflow-y-auto border-l border-slate-200 dark:border-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/80 backdrop-blur-md px-6 py-4 dark:border-slate-800 dark:bg-slate-900/80">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Payment Request</p>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${tone.bg} ${tone.text} ring-1 ring-inset ${tone.ring}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
                {SEND_STATUS_LABELS[lpr.sendStatus]}
              </span>
              <span className="ml-2 font-mono text-xs">{ROUTING_LABELS[lpr.routing]}</span>
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {canForceLupsCharge && (
              <button
                onClick={handleForceLupsCharge}
                disabled={forcing}
                title="⚠️ Solo dev/QA — genera la orden de pago en Lups y dispara el cargo a la TC OneClick"
                className="inline-flex items-center gap-1.5 rounded-md border border-purple-300 bg-purple-50 px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-purple-700 hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed dark:border-purple-800 dark:bg-purple-950/40 dark:text-purple-300 dark:hover:bg-purple-950/60"
              >
                <span className={`material-symbols-outlined text-[14px] ${forcing ? 'animate-spin' : ''}`}>{forcing ? 'progress_activity' : 'bolt'}</span>
                {forcing ? 'cobrando…' : 'Forzar cobro Lups'}
              </button>
            )}
            <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <Field label="id" value={lpr.id} mono />
          <Field label="advanceRequestId" value={lpr.advanceRequestId} mono />
          <Field label="installmentDetailId" value={lpr.installmentDetailId} mono />
          <Field label="lupsRequestId" value={lpr.lupsRequestId ?? '—'} mono />
          <Field label="connectorId" value={lpr.connectorId ?? '—'} mono />
          <Field label="Monto" value={`$${Number(lpr.monto).toLocaleString('es-CL')}`} />
          <Field label="Fecha objetivo" value={formatDate(lpr.fechaObjetivo)} />
          <Field label="Fecha de abandono" value={formatDate(lpr.abandonmentDate)} />
          <Field label="Intentos envío" value={String(lpr.sendAttempts)} />
          <Field label="Intentos cobro" value={String(lpr.collectionAttempts)} />

          {lpr.company && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">Comercio</p>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">RUT</span>
                  <span className="font-mono text-slate-700 dark:text-slate-300">{lpr.company.taxId ?? '—'}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">Razón social</span>
                  <span className="text-slate-700 dark:text-slate-300 text-right">{lpr.company.businessName ?? '—'}</span>
                </div>
              </div>
            </div>
          )}

          {lpr.installment && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">Cuota</p>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">Número</span>
                  <span className="font-mono text-slate-700 dark:text-slate-300">#{lpr.installment.number}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">Vencimiento</span>
                  <span className="font-mono text-slate-700 dark:text-slate-300">{formatDate(lpr.installment.dueAt)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-slate-500">Payment amount</span>
                  <span className="font-mono text-slate-700 dark:text-slate-300">${lpr.installment.paymentAmount ? Number(lpr.installment.paymentAmount).toLocaleString('es-CL') : '—'}</span>
                </div>
              </div>
            </div>
          )}

          {lpr.routing === 'pac' && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/30">
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-400 mb-3">Estado PAC</p>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between gap-4">
                  <span className="text-amber-700 dark:text-amber-400">Mandato</span>
                  <code className="font-mono text-amber-900 dark:text-amber-300 truncate max-w-[260px]">{lpr.pacMandateId ?? '—'}</code>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-amber-700 dark:text-amber-400">Reintentos PAC</span>
                  <span className="font-mono text-amber-900 dark:text-amber-300">{lpr.pacRetryCount}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-amber-700 dark:text-amber-400">Último código fallo</span>
                  <code className="font-mono text-amber-900 dark:text-amber-300">{lpr.pacLastFailureCode ?? '—'}</code>
                </div>
              </div>
            </div>
          )}

          {(lpr.collectedAt || lpr.abandonedAt || lpr.disabledAt || lpr.deletedAt) && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">Timeline</p>
              <div className="space-y-2 text-xs">
                {lpr.collectedAt && (
                  <div className="flex justify-between gap-4">
                    <span className="text-emerald-700 dark:text-emerald-400">Cobrado</span>
                    <span className="font-mono text-slate-700 dark:text-slate-300">{formatDateTime(lpr.collectedAt)}</span>
                  </div>
                )}
                {lpr.abandonedAt && (
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-500">Abandonado</span>
                    <span className="font-mono text-slate-700 dark:text-slate-300">{formatDateTime(lpr.abandonedAt)}</span>
                  </div>
                )}
                {lpr.disabledAt && (
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-500">Deshabilitado{lpr.disableReason ? ` (${lpr.disableReason})` : ''}</span>
                    <span className="font-mono text-slate-700 dark:text-slate-300">{formatDateTime(lpr.disabledAt)}</span>
                  </div>
                )}
                {lpr.deletedAt && (
                  <div className="flex justify-between gap-4">
                    <span className="text-slate-500">Eliminado{lpr.deleteResponseStatus ? ` (HTTP ${lpr.deleteResponseStatus})` : ''}</span>
                    <span className="font-mono text-slate-700 dark:text-slate-300">{formatDateTime(lpr.deletedAt)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {lpr.lastError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-900/50 dark:bg-rose-950/30">
              <p className="font-mono text-[10px] uppercase tracking-widest text-rose-700 dark:text-rose-400 mb-2">Último error</p>
              <pre className="overflow-x-auto font-mono text-[11px] text-rose-900 dark:text-rose-300 whitespace-pre-wrap">{lpr.lastError}</pre>
            </div>
          )}

          {lpr.events.length > 0 && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Eventos Lups asociados</p>
              <div className="space-y-1">
                {lpr.events.map((evt) => (
                  <div key={evt.idEvento} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-800 dark:bg-slate-900">
                    <code className="font-mono text-[10px] text-slate-500 truncate" title={evt.idEvento}>{evt.idEvento}</code>
                    <span className={`font-mono text-[10px] ${evt.eventType === 'payment_succeeded' ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
                      {evt.eventType}
                    </span>
                    <span className={`font-mono text-[10px] ${evt.processedAt ? 'text-slate-500' : 'text-amber-700 dark:text-amber-400'}`}>
                      {evt.processedAt ? 'procesado' : 'pendiente'}
                    </span>
                    <span className="font-mono text-[10px] text-slate-500">{formatDateTime(evt.timestampEvento)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 pb-2 dark:border-slate-800">
      <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
      <span className={mono ? 'font-mono text-xs text-slate-700 dark:text-slate-300 break-all text-right' : 'text-xs text-slate-700 dark:text-slate-300 text-right'}>{value}</span>
    </div>
  );
}
